<template>
  <div class="fee-selection-container">
    <!-- Add New Fee -->
    <div class="price-column">
      <span v-if="showTitle">{{ $t('OrderPending.orderTab.addOdooFee') }}</span>
      <regular-select
        :value="selectedFeeType"
        @change="handleFeeSelection"
        :placeholder="$t('OrderPending.orderTab.selectFeeType')"
        class="form-group"
      >
        <option
          v-for="fee in availableFees"
          :key="fee.value"
          :value="fee.value"
        >
          {{ fee.text }}
        </option>
      </regular-select>
    </div>

    <!-- Selected Fees List -->
    <div v-if="selectedFees.length > 0" class="selected-fees">
      <h4>{{ $t('OrderPending.orderTab.selectedFees') }}</h4>
      <div
        v-for="(selectedFee, index) in selectedFees"
        :key="selectedFee.type"
        class="selected-fee-item"
      >
        <div class="fee-info">
          <span class="fee-type">{{ selectedFee.text }}</span>
          <div class="amount-input-group">
            <input
              type="number"
              v-model="selectedFee.amount"
              :placeholder="$t('OrderPending.orderTab.enterAmount')"
              class="amount-input"
              min="0"
              step="0.01"
            />
            <button
              @click="removeFee(index)"
              class="remove-btn"
              type="button"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="totalFeeAmount > 0" class="total-fee">
      <strong>{{ $t('OrderPending.orderTab.totalFees') }}: {{ totalFeeAmount + ' $'}}</strong>
    </div>
  </div>
</template>

<script>
import RegularSelect from './regularSelect.vue'

export default {
  name: 'FeeSelector',
  components: {
    RegularSelect
  },
  props: {
    initialFees: {
      type: Array,
      default: () => []
    },
    showTitle: {
      default: true,
      required: false,
      type: Boolean
    }
  },
  data () {
    return {
      selectedFeeType: '',
      selectedFees: [],
      allOrderFees: [
        {
          text: this.$t('OrderPending.orderTab.storageFee'),
          value: 'Storage Fee'
        },
        {
          text: this.$t('OrderPending.orderTab.waitingTime'),
          value: 'Waiting Fee'
        },
        {
          text: this.$t('OrderPending.orderTab.dryRun'),
          value: 'Dry Run'
        },
        {
          text: this.$t('OrderPending.orderTab.other'),
          value: 'Other'
        }
      ]
    }
  },
  mounted () {
    if (this.initialFees && this.initialFees.length > 0) {
      this.selectedFees = [...this.initialFees]
    }
  },
  computed: {
    availableFees () {
      const selectedTypes = this.selectedFees.map(fee => fee.type)
      return this.allOrderFees.filter(fee => !selectedTypes.includes(fee.value))
    },

    totalFeeAmount () {
      return this.selectedFees.reduce((total, fee) => {
        return total + (parseFloat(fee.amount) || 0)
      }, 0)
    },

    formattedFeesArray () {
      return this.selectedFees.map(fee => ({
        type: fee.type,
        text: fee.text,
        amount: parseFloat(fee.amount) || 0
      }))
    }
  },
  methods: {
    handleFeeSelection (selectedValue) {
      if (selectedValue) {
        const selectedFeeOption = this.allOrderFees.find(fee => fee.value === selectedValue)

        this.selectedFees.push({
          type: selectedValue,
          text: selectedFeeOption.text,
          amount: 0
        })

        this.selectedFeeType = ''

        this.$emit('fees-updated', this.formattedFeesArray)
      }
    },

    removeFee (index) {
      this.selectedFees.splice(index, 1)
      this.$emit('fees-updated', this.formattedFeesArray)
    }
  },
  watch: {
    selectedFees: {
      deep: true,
      handler () {
        this.$emit('fees-updated', this.formattedFeesArray)
      }
    }
  }
}
</script>

<style scoped>
.fee-selection-container {
  margin-bottom: 20px;
}

.selected-fees {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.selected-fees h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
}

.selected-fee-item {
  margin-bottom: 15px;
  padding: 12px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.selected-fee-item:last-child {
  margin-bottom: 0;
}

.fee-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.fee-type {
  font-weight: 500;
  color: #333;
  flex: 1;
  min-width: 120px;
}

.amount-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amount-input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 120px;
  font-size: 14px;
}

.amount-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.remove-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}

.remove-btn:hover {
  background-color: #c82333;
}

.price-column {
  margin-bottom: 15px;
}

.price-column span {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.total-fee {
  margin-top: 15px;
  padding: 10px;
  background-color: #e7f3ff;
  border-radius: 4px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .fee-info {
    flex-direction: column;
    align-items: stretch;
  }

  .amount-input-group {
    justify-content: space-between;
  }

  .amount-input {
    flex: 1;
  }
}
</style>
